import {
  hostWall,
  retainingWallFace,
  wallAngle,
  type RetainingColumn,
  type RetainingWall,
} from '@obraplan/core/domain/takeoff/retaining';
import type { Point } from '@obraplan/core/domain/takeoff/polygon';
import { metros } from './formato';
import { proximoNomeDaPeca } from './fundacao-da-prancha';

/**
 * As contas do muro e do pilar de contenção na prancha, sem tela (Fase 27, etapa 4 — D9, F6,
 * RN-372).
 *
 * Moram à parte do desenho para que o que a prancha diz — o próximo nome, o encaixe do clique
 * sobre o muro, o ângulo que o pilar copia, o rótulo com a face — seja número que se testa
 * sem montar a prancha.
 */

/**
 * As cores do muro e do pilar de contenção, na prancha e na legenda. Não seguem o tema, pelo
 * mesmo motivo das do resto do traçado: a prancha é papel branco.
 */
export const COR_DO_MURO = '#b45309';
export const COR_DO_PILAR_DE_CONTENCAO = '#be123c';

export const proximoNomeDeMuro = (muros: readonly { readonly name: string }[]): string =>
  proximoNomeDaPeca(muros, 'MC');

export const proximoNomeDePilarDeContencao = (
  pilares: readonly { readonly name: string }[],
): string => proximoNomeDaPeca(pilares, 'PC');

type MuroNoDesenho = Pick<RetainingWall, 'from' | 'to' | 'thickness' | 'deletedAt'>;

/** Onde o pilar cai: o centro, o ângulo da seção e o muro em que ficou encaixado, se ficou. */
export interface PilarEncaixado<W extends MuroNoDesenho> {
  readonly centro: Point;
  readonly angulo: number;
  readonly muro: W | undefined;
}

/**
 * O clique do pilar, encaixado no muro quando cai sobre um (RN-372).
 *
 * Sobre o muro, o centro vai para o **eixo** dele — o pé da perpendicular — e o ângulo copia
 * o do muro. Fora de muro, o ponto fica onde foi clicado e o ângulo é o `anguloSolto`: zero
 * para o pilar que nasce, o que ele já tinha para o pilar arrastado para fora.
 */
export function encaixarNoMuro<W extends MuroNoDesenho>(
  ponto: Point,
  muros: readonly W[],
  anguloSolto = 0,
): PilarEncaixado<W> {
  const muro = hostWall({ center: ponto }, muros);
  if (muro === undefined) return { centro: ponto, angulo: anguloSolto, muro: undefined };
  const dx = muro.to.x - muro.from.x;
  const dy = muro.to.y - muro.from.y;
  const t = Math.min(
    1,
    Math.max(
      0,
      ((ponto.x - muro.from.x) * dx + (ponto.y - muro.from.y) * dy) / (dx * dx + dy * dy),
    ),
  );
  return {
    centro: { x: muro.from.x + t * dx, y: muro.from.y + t * dy },
    angulo: wallAngle(muro),
    muro,
  };
}

/** Os quatro cantos do retângulo de um pilar, girado no ângulo da seção. Genérico: metro ou pixel. */
export function cantosDoPilar(
  centro: Point,
  largura: number,
  profundidade: number,
  angulo: number,
): readonly Point[] {
  const radianos = (angulo * Math.PI) / 180;
  const cos = Math.cos(radianos);
  const sen = Math.sin(radianos);
  const meioL = largura / 2;
  const meioP = profundidade / 2;
  return [
    [-meioL, -meioP],
    [meioL, -meioP],
    [meioL, meioP],
    [-meioL, meioP],
  ].map(([u, v]) => ({
    x: centro.x + u! * cos - v! * sen,
    y: centro.y + u! * sen + v! * cos,
  }));
}

/** "30 × 50" — a seção como a obra fala: ao longo do muro e na direção da espessura, em cm. */
export function secaoDoPilarDeContencao(
  pilar: Pick<RetainingColumn, 'sectionWidth' | 'sectionDepth'>,
): string {
  return `${String(Math.round(pilar.sectionWidth * 100))} × ${String(Math.round(pilar.sectionDepth * 100))}`;
}

/** O rótulo do muro na prancha: o nome e a face em elevação, sem o que os pilares ocupam. */
export function rotuloDoMuro(
  muro: Pick<RetainingWall, 'name' | 'from' | 'to' | 'height' | 'thickness'>,
  pilares: readonly Pick<
    RetainingColumn,
    'center' | 'sectionWidth' | 'sectionDepth' | 'height' | 'deletedAt'
  >[],
): string {
  return `${muro.name} · face ${metros(retainingWallFace(muro, pilares))} m²`;
}

/** O pilar está solto — não cai sobre nenhum muro vivo? */
export function pilarSolto(
  pilar: Pick<RetainingColumn, 'center'>,
  muros: readonly MuroNoDesenho[],
): boolean {
  return hostWall(pilar, muros) === undefined;
}
